import * as styles from "./ColorPickerWheel.css";

import { clsx } from "clsx";
import { useCallback, useRef } from "react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";

import { useColorPickerContext } from "./ColorPicker.context";
import type { ColorPickerWheelProps } from "./ColorPicker.types";
import { hsvToHex } from "./colorMath";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wrapHue(hue: number): number {
  return ((hue % 360) + 360) % 360;
}

/** Angle gives the hue (0° at the top, clockwise), radius the saturation. */
function hsFromPoint(clientX: number, clientY: number, rect: DOMRect) {
  const dx = clientX - (rect.left + rect.width / 2);
  const dy = clientY - (rect.top + rect.height / 2);
  const radius = rect.width / 2;

  return {
    h: wrapHue((Math.atan2(dx, -dy) * 180) / Math.PI),
    s: clamp((Math.hypot(dx, dy) / radius) * 100, 0, 100),
  };
}

export function ColorPickerWheel({
  className,
}: ColorPickerWheelProps): JSX.Element {
  const { hsv, size, disabled, setHsv } = useColorPickerContext();

  const wheelRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = wheelRef.current;

      if (!el) {
        return;
      }

      setHsv(hsFromPoint(clientX, clientY, el.getBoundingClientRect()));
    },
    [setHsv],
  );

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    draggingRef.current = true;
    wheelRef.current?.focus();
    updateFromPointer(event.clientX, event.clientY);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) {
      return;
    }

    // Dragging past the edge keeps turning the hue, saturation just pins at 100.
    updateFromPointer(event.clientX, event.clientY);
  }

  function stopDragging(event: PointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    draggingRef.current = false;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }

    const step = event.shiftKey ? 10 : 1;

    const nextByKey: Record<string, Partial<{ h: number; s: number }>> = {
      ArrowLeft: { h: wrapHue(hsv.h - step) },
      ArrowRight: { h: wrapHue(hsv.h + step) },
      PageDown: { h: wrapHue(hsv.h - 30) },
      PageUp: { h: wrapHue(hsv.h + 30) },
      ArrowDown: { s: clamp(hsv.s - step, 0, 100) },
      ArrowUp: { s: clamp(hsv.s + step, 0, 100) },
      Home: { s: 0 },
      End: { s: 100 },
    };

    const next = nextByKey[event.key];

    if (next) {
      event.preventDefault();
      setHsv(next);
    }
  }

  const hue = Math.round(hsv.h);
  const saturation = Math.round(hsv.s);
  const radians = (hsv.h * Math.PI) / 180;
  // The thumb rides at s% of the radius, and the radius is 50% of the box.
  const distance = hsv.s / 2;

  return (
    <div
      ref={wheelRef}
      data-slot="color-picker-wheel"
      data-disabled={disabled || undefined}
      className={clsx(styles.wheel({ size }), className)}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Teinte et saturation"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={saturation}
      aria-valuetext={`Teinte ${hue}°, saturation ${saturation}%`}
      aria-disabled={disabled || undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.shade} style={{ opacity: 1 - hsv.v / 100 }} />
      <div
        className={styles.thumb}
        style={{
          left: `${50 + Math.sin(radians) * distance}%`,
          top: `${50 - Math.cos(radians) * distance}%`,
          backgroundColor: hsvToHex(hsv),
        }}
      />
    </div>
  );
}
